// Small wrapper around fetch() so the pages don't need to know the API's
// base URL or how to parse error responses.
//
// REACT_APP_API_URL is baked in at build time (see frontend/Dockerfile),
// so the Docker build can point this at the nginx-proxied "/api" path
// instead of a hardcoded host. Local `npm start` keeps talking straight
// to the Go server on :8080.
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

// The origin the backend serves uploaded files from (see
// infrastructure/storage on the backend) — issue image_path values are
// URL paths relative to this, not to API_BASE_URL.
export const API_ORIGIN = API_BASE_URL.replace(/\/api$/, "");

export async function request(path, { method = "GET", body, token } = {}) {
  // FormData (issue cover uploads) must not be JSON-encoded or given an
  // explicit Content-Type — fetch sets the multipart boundary itself.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const headers = {};
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  // DELETE responses (204 No Content) have no body — response.json() would
  // throw on them, so parse manually and fall back to {}. A non-JSON body
  // (e.g. nginx's own bare HTML page for a 413/502/504 that never reached
  // the Go backend at all) is treated the same as an empty one, rather
  // than letting JSON.parse's SyntaxError escape as the thrown error.
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error("uploaded file is too large");
    }
    throw new Error(data.error || "unknown error");
  }

  return data;
}

// login is used only by the admin login gate (see components/AdminRoute.js)
// — there's no public signup, so this is the sole way to obtain a JWT.
export function login(username, password) {
  return request("/login", { method: "POST", body: { username, password } });
}

// Used by the admin panel's account settings page to replace the password
// set via ADMIN_PASSWORD at bootstrap (see backend/cmd/api/main.go).
export function changePassword(token, currentPassword, newPassword) {
  return request("/account/password", {
    method: "PUT",
    token,
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

// Used by the admin panel's "new admin" page to provision another admin
// account, instead of the only way being ADMIN_USERNAME/ADMIN_PASSWORD at
// first startup (see backend/cmd/api/main.go's bootstrapAdmin).
export function createAdmin(token, name, username, password) {
  return request("/admins", {
    method: "POST",
    token,
    body: { name, username, password },
  });
}

// The backend's error strings are plain English (see backend/internal/...).
// This maps the ones the auth forms can actually receive to Persian; any
// message we don't recognize falls back to a generic Persian error.
const ERROR_TRANSLATIONS = {
  "missing Authorization header": "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.",
  "Authorization header must be a Bearer token": "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.",
  "invalid or expired token": "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.",
  "invalid username or password": "نام کاربری یا رمز عبور اشتباه است.",
  "current password is incorrect": "رمز عبور فعلی اشتباه است.",
  "new password must be at least 8 characters": "رمز عبور جدید باید حداقل ۸ کاراکتر باشد.",
  "could not change password": "تغییر رمز عبور با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "name and username are required": "نام و نام کاربری الزامی هستند.",
  "password must be at least 8 characters": "رمز عبور باید حداقل ۸ کاراکتر باشد.",
  "username is already taken": "این نام کاربری قبلاً استفاده شده است.",
  "could not create admin user": "ایجاد حساب مدیر با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "invalid request body": "اطلاعات ارسال‌شده نامعتبر است.",
  "could not log in": "ورود با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "title and body are required": "عنوان و متن مقاله الزامی هستند.",
  "article not found": "مقاله موردنظر یافت نشد.",
  "invalid article id": "شناسهٔ مقاله نامعتبر است.",
  "admin access required": "شما دسترسی مدیریت ندارید.",
  "could not create article": "ایجاد مقاله با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not update article": "ویرایش مقاله با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not delete article": "حذف مقاله با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not load articles": "بارگذاری مقاله‌ها با خطا مواجه شد.",
  "could not load article": "بارگذاری مقاله با خطا مواجه شد.",
  "slug, title, subtitle, description and icon are required": "همهٔ فیلدهای نشریه الزامی هستند.",
  "invalid icon key": "آیکون انتخاب‌شده نامعتبر است.",
  "category, title and description are required": "دسته، عنوان و توضیح کارت الزامی هستند.",
  "invalid category": "دستهٔ انتخاب‌شده نامعتبر است.",
  "invalid topic id": "شناسهٔ کارت نامعتبر است.",
  "slug is already taken": "این نامک قبلاً استفاده شده است.",
  "publication not found": "نشریه موردنظر یافت نشد.",
  "topic not found": "کارت موردنظر یافت نشد.",
  "could not load publications": "بارگذاری نشریه‌ها با خطا مواجه شد.",
  "could not load publication": "بارگذاری نشریه با خطا مواجه شد.",
  "could not load topic": "بارگذاری کارت با خطا مواجه شد.",
  "could not create publication": "ایجاد نشریه با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not update publication": "ویرایش نشریه با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not delete publication": "حذف نشریه با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not create topic": "ایجاد کارت با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not update topic": "ویرایش کارت با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not delete topic": "حذف کارت با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "invalid issue number": "شمارهٔ واردشده نامعتبر است.",
  "cover image is required": "انتخاب تصویر جلد الزامی است.",
  "unsupported image type — allowed: png, jpg, jpeg, webp": "فرمت تصویر پشتیبانی نمی‌شود (فقط png، jpg و webp).",
  "image file is required": "انتخاب تصویر الزامی است.",
  "uploaded file is too large": "حجم فایل انتخاب‌شده بیش از حد مجاز (۲۵ مگابایت) است.",
  "number and title are required, and number must be positive": "شماره و عنوان الزامی‌اند و شماره باید مثبت باشد.",
  "issue number is already taken": "این شماره قبلاً ثبت شده است.",
  "invalid issue id": "شناسهٔ شماره نامعتبر است.",
  "issue not found": "شمارهٔ موردنظر یافت نشد.",
  "could not load issues": "بارگذاری شماره‌ها با خطا مواجه شد.",
  "could not create issue": "بارگذاری شماره با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not delete issue": "حذف شماره با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "issue topic not found": "کارت موردنظر یافت نشد.",
  "invalid position": "جایگاه نامعتبر است.",
  "position must be 1, 2, or 3": "جایگاه باید ۱، ۲ یا ۳ باشد.",
  "could not load featured cards": "بارگذاری کارت‌های منتخب با خطا مواجه شد.",
  "could not set featured card": "ثبت کارت منتخب با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not clear featured card": "حذف کارت منتخب با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not load pinned cards": "بارگذاری کارت‌های پین‌شده با خطا مواجه شد.",
  "could not pin card": "پین‌کردن کارت با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "pinned card not found": "این کارت در فهرست پین‌شده‌ها یافت نشد.",
  "kind must be topic or issue_topic": "نوع کارت نامعتبر است.",
  "invalid card id": "شناسهٔ کارت نامعتبر است.",
  "could not unpin card": "حذف پین با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
  "could not reorder pinned cards": "تغییر ترتیب کارت‌های پین‌شده با خطا مواجه شد.",
  "could not search": "جست‌وجو با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
};

export function translateAuthError(message) {
  return ERROR_TRANSLATIONS[message] || "خطایی رخ داد. لطفاً دوباره تلاش کنید.";
}
